/**
 * Accept, on an assignment ask (migration 981; PRD-assignment-record.md FR-9).
 *
 * get_responsibilities names the act on the card itself: valid action
 * ACCEPT_ASSIGNMENT → accept_assignment, with the revision id in its args. This
 * draws that act as a button that performs it, through acceptAssignment (the
 * same call the Registry page makes), never as a link that only navigates
 * (build contract §2.8). The ask is finished by its subject: after Accept the
 * caller reads the engine again and the card is gone; a refusal shows the
 * database's own sentence. A response with terms offered (1028) carries Accept the
 * new terms the same way, for the person who made the request.
 */
import { useState, type CSSProperties } from 'react';
import { acceptAssignment, answerAssignmentResponse, assignmentAcceptOf } from '@/lib/assignmentService';
import type { ResponsibilityCard } from '@/types/prioritization/responsibility';

interface Props {
  card: ResponsibilityCard;
  className?: string;
  style?: CSSProperties;
  /** Called after the revision binds; the caller reads the engine again. */
  onDone: () => void | Promise<void>;
}

export function AssignmentAskAccept({ card, className = '', style, onDone }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = assignmentAcceptOf(card);
  if (!accept) return null;

  async function run() {
    if (!accept) return;
    setBusy(true);
    setError(null);
    const res = 'responseId' in accept
      ? await answerAssignmentResponse(accept.responseId, 'accept')
      : await acceptAssignment(accept.revisionId, null);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await onDone();
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        aria-busy={busy}
        className={`${className} disabled:opacity-50`}
        style={style}
        data-testid="assignment-ask-accept"
      >
        {busy ? 'Accepting…' : accept.label}
      </button>
      {error && (
        <span role="alert" className="text-sm text-[var(--coral-text)]">
          {error}
        </span>
      )}
    </span>
  );
}
